<template>
  <Head :title="`${t('page.heading')} - ${assignment.name} - ${course.code}`"></Head>
  <s-page-block class="@container">
    <s-page-header :heading="t('page.heading')" class="mb-4">
      <template #actions>
        <s-btn
          icon="table-arrow-down"
          :href="route('courses.assignments.issued-task-states.download', getRouteParams())"
          :only="dataThatChanges"
          preserve-state
          external
          target="_blank"
          color="blue-light"
        >
          {{ t('exportTaskStates') }}
        </s-btn>
        <s-btn
          v-if="canViewAnalytics"
          :href="route('courses.assignments.analytics.grades', getRouteParams())"
          color="secondary"
          icon="poll"
        >
          {{ t('viewAnalytics') }}
        </s-btn>
        <s-btn
          icon="table-arrow-down"
          :href="route('courses.assignments.grades.download', getRouteParams())"
          :only="dataThatChanges"
          preserve-state
          external
          target="_blank"
          color="blue-light"
        >
          {{ t('exportGrades') }}
        </s-btn>
        <!-- TODO probably just want to show this if the assignment policy is to review tasks -->
        <s-btn
          v-if="assignment.reviewRequired"
          icon="pencil"
          :href="
            route('courses.assignments.marking.review.last', [props.course.id, props.assignment.id])
          "
          preserve-state
        >
          {{ t('goToLastReviewed') }}
        </s-btn>
      </template>
    </s-page-header>

    <s-search-form
      class="mb-4"
      route-name="courses.assignments.students.index"
      :label="t('search.byNameOrEmail')"
    />

    <s-filter-group class="mb-4">
      <s-select-filter
        id="section"
        v-model="params.sectionId"
        @update:modelValue="submitSearch({sectionId: $event})"
        :label="t('allSections')"
        :active-label="t('section')"
        :options="
          sections.map((section) => ({
            value: section.id,
            label: section.name,
          }))
        "
      />
      <s-select-filter
        id="progress"
        v-model="params.progress"
        @update:modelValue="submitSearch({progress: $event})"
        :label="t('studentProgress')"
        :options="statusList"
      />
      <s-select-filter
        v-if="assignment.reviewRequired"
        id="review"
        v-model="params.review"
        @update:modelValue="submitSearch({review: $event})"
        :label="t('reviewStatus')"
        :options="statusList"
      />
      <s-number-filter id="gradeMax" v-model="gradeMaximum" :label="t('maxGrade')" :suffix="t('%')">
        <template #value>
          {{ t('xPercent', gradeMaximum) }}
        </template>
      </s-number-filter>
    </s-filter-group>

    <section class="stack vertical">
      <div class="w-[calc(100%_+_1.5rem)] overflow-auto -mx-3 p-3 -mt-3 mb-3">
        <s-data-table
          v-model:sort-by="sortBy"
          dense="denser"
          :td="{class: 'cursor-pointer', click: navigateToFirstTaskForStudent}"
          :columns="[
            {key: 'lastName', label: t('lastName'), sortable: true},
            {key: 'firstName', label: t('firstName'), sortable: true},
            {
              key: 'email',
              label: t('email'),
              sortable: true,
              td: {class: 'cursor-pointer min-w-0'},
            },
            {key: 'sections', label: t('section'), sortable: true},
            {
              key: 'grade',
              label: t('grade'),
              sortable: true,
              td: {class: '', click: null},
            },
            {key: 'numTasksSubmitted', label: t('tableHeaders.submitted'), sortable: true},
            {
              key: 'numReviewedGrades',
              label: t('tableHeaders.reviewed'),
              sortable: true,
              hidden: !assignment.reviewRequired,
            },
          ]"
          :data="participants.data"
        >
          <template #empty>
            {{ t('noResultsToDisplay') }}
          </template>

          <template #td:lastName="participant">
            <s-link
              :href="
                route('courses.assignments.marking.tasks.first', {
                  course: course.id,
                  assignment: assignment.id,
                  user: participant.id,
                  _query,
                })
              "
              class="link"
            >
              {{ participant.lastName }}
            </s-link>
          </template>
          <template #td:firstName="participant">
            <s-link
              :href="
                route('courses.assignments.marking.tasks.first', {
                  course: course.id,
                  assignment: assignment.id,
                  user: participant.id,
                  _query,
                })
              "
              class="link"
            >
              {{ participant.firstName }}
            </s-link>
          </template>
          <template #td:grade="participant">
            <s-link
              class="z-100"
              :href="
                participant.grade?.id
                  ? route('assignment_grades.adjustments.create', {
                      grade: participant.grade?.id,
                    })
                  : route('courses.assignments.marking.tasks.first', {
                      course: course.id,
                      assignment: assignment.id,
                      user: participant.id,
                      _query,
                    })
              "
              :disabled="!participant.grade?.id"
            >
              <span class="tracking-wide flex items-center gap-1">
                <s-grade
                  :grade="participant.grade"
                  :grade-display="course.gradeDisplay"
                  tooltip
                ></s-grade>
                <s-icon
                  v-if="participant.grade"
                  class="font-bold text-blue-500 tracking-wide flex items-center gap-1"
                  name="pencil"
                  size="14"
                />
              </span>
            </s-link>
          </template>
          <template #td:numTasksSubmitted="participant">
            <div class="flex justify-start -my-1.5">
              <s-badge
                :color="progressStatus(participant.numTasksSubmitted, totalTasks)"
                class="leading-tight"
              >
                <span :class="participant.numTasksSubmitted! < 1 && 'text-gray-400'">
                  {{ participant.numTasksSubmitted ?? 0 }} / {{ totalTasks ?? 0 }}
                </span>
              </s-badge>
            </div>
          </template>
        </s-data-table>
      </div>
      <s-paginator :paginator="participants" :only="dataThatChanges" preserve-state />
    </section>
  </s-page-block>
</template>
<script setup lang="ts">
import {Head, router, usePage} from '@inertiajs/vue3';
import SLink from '../../components/SLink.vue';
import {route} from 'ziggy-js';
import {computed, reactive, ref, watch} from 'vue';
import {Course} from '../../types/entities/course';
import {Assignment} from '../../types/entities/assignment';
import SPageBlock from '../../design-system/SPageBlock.vue';
import SSelectField from '../../design-system/SSelectField.vue';
import SSearchSection from '../../design-system/SSearchSection.vue';
import SPaginator from '../../design-system/SPaginator.vue';
import SGrade from '../../components/SGrade.vue';
import SBadge from '../../design-system/SBadge.vue';
import {LaravelPaginator} from '../../types/laravel-paginator';
import SBtn from '../../design-system/SBtn.vue';
import SPageHeader from '../../design-system/SPageHeader.vue';
import {usePageParams} from '../../composables/usePageParams';
import AssignmentParticipantDto = App.DTOs.AssignmentParticipantDto;
import {useI18n} from 'vue-i18n';
import {useBreadcrumbs} from '../../composables/useBreadcrumbs';
import SIcon from '../../design-system/SIcon.vue';
import {useDebounceFn} from '@vueuse/core';
import SDataTable, {SortBy} from '../../design-system/SDataTable.vue';
import SSearchForm from '../../design-system/SSearchForm.vue';
import SFilterGroup from '../../design-system/SFilterGroup.vue';
import SSelectFilter from '../../design-system/filters/SSelectFilter.vue';
import SNumberFilter from '../../design-system/filters/SNumberFilter.vue';

type PageParams = {
  orderByDirection?: 'ASC' | 'DESC';
  orderBy?: string;
  search?: string;
  sectionId?: string | number | null;
  roleId?: string;
  review?: string | number | null;
  progress?: string | number | null;
  gradeBetween?: string;
};

const props = defineProps<{
  course: Course;
  assignment: Assignment;
  sections: {id: string; name: string}[];
  participants: LaravelPaginator<AssignmentParticipantDto>;
  totalTasks: number;
  numTasksToReview: number;
  canViewAnalytics: boolean;
}>();

const params = usePageParams<PageParams>({
  sectionId: undefined,
  review: undefined,
  progress: undefined,
});

/* TODO: this currently supports both ends of range, but we should work on the interaction between filters
         before implementing the minimum. The search component assumes parameters come from the page params, which
         assumes that each filter change is triggering a page load, but that is a lot if quickly editing. */
const gradeBounds = computed(() => {
  if (!params.gradeBetween) {
    return ['', ''];
  }

  const [low, high] = params.gradeBetween.split(',', 2);
  return [low || '', parseInt(high || '')];
});

const gradeMaximum = computed({
  get: () => gradeBounds.value[1] as number,
  set: (value: number) => {
    if (!value) {
      debouncedSubmitSearch({gradeBetween: undefined});
    } else {
      debouncedSubmitSearch({gradeBetween: `,${value}`});
    }
  },
});

const {t} = useI18n();

const _query = computed(() => {
  if (params.sectionId) {
    return {sectionFilter: params.sectionId};
  }
});

useBreadcrumbs([
  {
    label: props.course.code,
    href: route('courses.show', props.course.id),
  },
  {
    label: t('assignments'),
    href: route('courses.assignments.index', {course: props.course.id}),
  },
  {
    label: props.assignment.name,
    href: route('courses.assignments.show', [props.course.id, props.assignment.id]),
  },
]);

const orderByLabels = {
  lastName: t('lastName'),
  firstName: t('firstName'),
  email: t('email'),
  grade: t('grade'),
  sections: t('section'),
  numTasksSubmitted: t('submitted'),
  numReviewedGrades: t('reviewed'),
};

const orderByLabel = computed((): string => {
  const orderByKey = params.orderBy as keyof typeof orderByLabels;
  return orderByLabels[orderByKey] || t('lastName');
});

const sortBy = ref<SortBy>({
  key: '',
  direction: 'ASC',
});

watch(sortBy, (sortBy) => {
  submitSearch({
    orderBy: sortBy.key,
    orderByDirection: sortBy.direction,
  });
});

const submitSearch = (params: Partial<PageParams>) => {
  router.visit(
    route('courses.assignments.students.index', {
      ...getRouteParams(),
      ...params,
    }),
    {
      only: dataThatChanges,
      preserveState: true,
    }
  );
};
const debouncedSubmitSearch = useDebounceFn(submitSearch, 1000);

const navigateToFirstTaskForStudent = (event: MouseEvent) => {
  const target = (event.target as HTMLElement).closest('[data-id]');
  if (!target) return;

  const studentId = target.getAttribute('data-id');
  if (!studentId) return;

  const routeToVisit = route('courses.assignments.marking.tasks.first', {
    course: props.course.id,
    assignment: props.assignment.id,
    user: studentId,
  });

  // Open in new tab if ctrl or cmd key is pressed
  if (event.ctrlKey || event.metaKey) {
    window.open(routeToVisit, '_blank');
  } else {
    // Otherwise, just use the router to click through.
    router.visit(routeToVisit);
  }
};

const getRouteParams = () => {
  return {
    course: props.course.id,
    assignment: props.assignment.id,
    ...params,
  };
};

const dataThatChanges = ['participants'];

const statusList: {value: string; label: string}[] = [
  {value: 'notStarted', label: t('notStarted')},
  {value: 'inProgress', label: t('inProgress')},
  {value: 'complete', label: t('complete')},
];

type MaybeMissingNumber = number | null | undefined;
const progressStatus = (num: MaybeMissingNumber, total: MaybeMissingNumber) => {
  num = num ?? 0;
  total = total ?? 0;

  if (num === 0) {
    return 'gray';
  }
  if (num >= total) {
    return 'green';
  }
  return 'blue';
};
</script>
<i18n>
{
  "en": {
    "page.heading": "Student Grades",
    "tableHeaders": {
      "submitted": "Submitted",
      "reviewed": "Reviewed"
    },
    "maxGrade": "Maximum Grade",
    "reviewStatus": "Review Status",
    "viewAnalytics": "View Analytics",
    "exportGrades": "Export Grades",
    "exportTaskStates": "Export Task States",
    "goToLastReviewed": "Go to last reviewed task",
    "noResultsToDisplay": "No Results to Display",
    "notStarted": "Not Started",
    "inProgress": "In Progress",
    "complete": "Complete",
    "section": "Section",
    "studentProgress": "Student Progress",
    "submitted": "Submitted",
    "reviewed": "Reviewed"
  },
  "fr": {
    "page.heading": "Notes des étudiants",
    "tableHeaders": {
      "submitted": "Soumis",
      "reviewed": "Revu"
    },
    "maxGrade": "Note Maximale",
    "reviewStatus": "Statut de révision",
    "viewAnalytics": "Afficher des analyses",
    "exportGrades": "Exporter des notes",
    "exportTaskStates": "Exporter les états de tâche",
    "goToLastReviewed": "Aller à la dernière tâche revu",
    "noResultsToDisplay": "Pas de résultats à afficher",
    "notStarted": "Non commencé",
    "inProgress": "En cours",
    "complete": "Terminé",
    "section": "Section",
    "studentProgress": "Progrès de l'étudiant",
    "submitted": "Soumis",
    "reviewed": "Révisé"
  }
}
</i18n>
